import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowSquareOut, SealCheck } from '@phosphor-icons/react'
import { DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import {
  subjectLink,
  useException,
  useExceptions,
  useReplacementDecision,
  type ExceptionRow,
} from '../api/exceptions'
import { ExceptionActions } from '../components/ExceptionActions'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { DetailDrawer } from '../components/DetailDrawer'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { ConsolePage, QueryState } from '../components/Layout'
import { PhotoViewer } from '../components/PhotoViewer'
import { Bi, Code, Facts, Severity, Status, UserName, When } from '../components/bits'
import { EXCEPTION_LABEL, exceptionLabel, humanize, statusLabel, subjectLabel } from '../labels'
import { useUrlState } from '../lib/useUrlState'

const LIMIT = 25
const SUBJECTS = [
  'UNIT',
  'CONTAINER',
  'SAMPLE',
  'SESSION',
  'SHIPMENT',
  'LOCATION',
  'PAYOUT',
  'DEVICE',
  'SYNC_COMMAND',
  'QR_CODE',
]

/** Exceptions queue (F4.11-g, F4-B2.c-c, F5.2-f): filter, open, resolve or waive. */
export function ExceptionsPage() {
  const [f, setF, { reset, active }] = useUrlState({
    status: 'OPEN',
    type: '',
    severity: '',
    subjectType: '',
    sessionId: '',
    unitId: '',
    runId: '',
    page: 1,
    open: '',
  })
  const q = useExceptions({
    page: f.page,
    limit: LIMIT,
    status: (f.status || undefined) as never,
    type: f.type || undefined,
    severity: (f.severity || undefined) as never,
    subjectType: f.subjectType || undefined,
    sessionId: f.sessionId || undefined,
    unitId: f.unitId || undefined,
    runId: f.runId || undefined,
  })

  const columns: Column<ExceptionRow>[] = [
    {
      id: 'severity',
      header: 'Severity',
      cell: (r) => <Severity severity={r.severity} />,
      width: '96px',
    },
    {
      id: 'type',
      header: 'Exception',
      cell: (r) => {
        const l = exceptionLabel(r.type)
        return (
          <span className="flex flex-col leading-tight">
            <span>{l.en}</span>
            {l.hi && (
              <span lang="hi" className="font-hindi text-[12px] text-muted">
                {l.hi}
              </span>
            )}
          </span>
        )
      },
    },
    {
      id: 'subject',
      header: 'About',
      cell: (r) => {
        const code = [r.detail.unit_code, r.detail.lot_code, r.detail.code, r.detail.payload].find(
          (x) => typeof x === 'string',
        ) as string | undefined
        return (
          <span className="flex flex-col">
            <span className="font-mono text-[12px]">
              {subjectLabel(r.subjectType)} · {code ?? r.subjectId.slice(0, 8)}
            </span>
            {r.farmerId && (
              <span className="text-[12px] text-muted">
                <UserName id={r.farmerId} />
              </span>
            )}
          </span>
        )
      },
      hideBelow: 'md',
    },
    {
      id: 'raised',
      header: 'Raised',
      cell: (r) => <When at={r.raisedAt} relative />,
      hideBelow: 'sm',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <Status
          status={r.status}
          label={
            r.resolutionCode
              ? `${statusLabel(r.status).en} · ${humanize(r.resolutionCode)}`
              : undefined
          }
        />
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Farm gate"
      title="Exceptions"
      description="Everything the gate, the store and the bank flagged. A business rule never rejects work — it raises one of these for a second person to settle."
    >
      <DataTable<ExceptionRow>
        caption="Exceptions"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => setF({ open: r.id, page: f.page })}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Status"
              value={(f.status === 'ALL' ? '' : f.status) as '' | 'OPEN'}
              onChange={(status) => setF({ status: status || 'ALL' })}
              options={[
                { value: 'OPEN', label: 'Open' },
                { value: 'RESOLVED', label: 'Resolved' },
                { value: 'WAIVED', label: 'Waived' },
              ]}
            />
            <SelectFilter
              label="Severity"
              value={f.severity}
              onChange={(severity) => setF({ severity })}
              options={['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((s) => ({
                value: s,
                label: s.toLowerCase(),
              }))}
            />
            <SelectFilter
              label="Type"
              value={f.type}
              onChange={(type) => setF({ type })}
              options={Object.entries(EXCEPTION_LABEL).map(([value, l]) => ({
                value,
                label: l.en,
              }))}
              className="min-w-[14rem]"
            />
            <SelectFilter
              label="About"
              value={f.subjectType}
              onChange={(subjectType) => setF({ subjectType })}
              options={SUBJECTS.map((s) => ({ value: s, label: subjectLabel(s) }))}
            />
            {(f.sessionId || f.unitId || f.runId) && (
              <button
                type="button"
                className="h-9 rounded-chip border border-rule px-2 font-mono text-[11px]"
                onClick={() => setF({ sessionId: '', unitId: '', runId: '' })}
              >
                {f.runId ? 'Run' : f.unitId ? 'Lot' : 'Day'} filter ✕
              </button>
            )}
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<SealCheck />}
            title="Nothing to settle"
            description="No exception matches these filters."
          />
        }
      />
      <ExceptionDrawer id={f.open || null} onClose={() => setF({ open: '', page: f.page })} />
    </ConsolePage>
  )
}

function ExceptionDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const q = useException(id)
  const e = q.data
  const l = e ? exceptionLabel(e.type) : null
  const link = e ? subjectLink(e) : null
  const photoKeys = e
    ? Object.entries(e.detail).filter(
        ([k, v]) => /photo/.test(k) && (typeof v === 'string' || Array.isArray(v)),
      )
    : []
  return (
    <DetailDrawer
      open={Boolean(id)}
      onClose={onClose}
      eyebrow={e ? `Exception · ${subjectLabel(e.subjectType)}` : 'Exception'}
      title={l ? <Bi en={l.en} hi={l.hi} /> : 'Exception'}
      meta={
        e && (
          <>
            <Severity severity={e.severity} />
            <Status status={e.status} />
          </>
        )
      }
      actions={e && <ExceptionActions exception={e} size="md" />}
    >
      <QueryState query={q} label="the exception">
        {e && (
          <div className="flex flex-col gap-5">
            <Facts
              columns={2}
              items={[
                { label: 'Raised', value: <When at={e.raisedAt} /> },
                {
                  label: 'Raised by',
                  value: e.raisedById ? <UserName id={e.raisedById} /> : 'System',
                },
                {
                  label: 'About',
                  value: link ? (
                    <Link className="inline-flex items-center gap-1 underline" to={link}>
                      {subjectLabel(e.subjectType)} <ArrowSquareOut aria-hidden="true" />
                    </Link>
                  ) : (
                    subjectLabel(e.subjectType)
                  ),
                },
                { label: 'Subject id', value: <Code>{e.subjectId}</Code> },
                ...(e.sessionId
                  ? [
                      {
                        label: 'Day',
                        value: (
                          <Link className="underline" to={`/c/sessions/${e.sessionId}`}>
                            Open the day
                          </Link>
                        ),
                      },
                    ]
                  : []),
                ...(e.farmerId
                  ? [
                      {
                        label: 'Farmer',
                        value: (
                          <Link className="underline" to={`/c/farmers/${e.farmerId}`}>
                            <UserName id={e.farmerId} />
                          </Link>
                        ),
                      },
                    ]
                  : []),
                ...(e.status !== 'OPEN'
                  ? [
                      { label: 'Closed by', value: <UserName id={e.resolvedById} /> },
                      { label: 'Closed', value: <When at={e.resolvedAt} /> },
                      {
                        label: 'Resolution',
                        value: e.resolutionCode ? humanize(e.resolutionCode) : '—',
                      },
                    ]
                  : []),
              ]}
            />
            {e.type === 'CONTAINER_REPLACEMENT' && e.status === 'OPEN' && (
              <ReplacementReview containerId={e.subjectId} />
            )}
            {photoKeys.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {photoKeys.flatMap(([k, v]) =>
                  (Array.isArray(v) ? v : [v]).map((p, i) => (
                    <PhotoViewer key={`${k}${i}`} photo={String(p)} alt={humanize(k)} />
                  )),
                )}
              </div>
            )}
            <DetailView detail={e.detail} />
          </div>
        )}
      </QueryState>
    </DetailDrawer>
  )
}

/** The exception's detail object, readable. */
function DetailView({ detail }: { detail: Record<string, unknown> }) {
  const entries = Object.entries(detail).filter(([k]) => !/photo/.test(k))
  if (!entries.length) return null
  return (
    <div>
      <div className="eyebrow mb-2">Detail</div>
      <dl className="divide-y divide-rule/70 rounded-card border border-rule bg-surface">
        {entries.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)] gap-3 px-3 py-2">
            <dt className="text-[13px] text-muted">{humanize(k)}</dt>
            <dd className="min-w-0 break-words font-mono text-[12.5px] text-ink">
              {v === null || v === undefined ? (
                '—'
              ) : typeof v === 'object' ? (
                <pre className="whitespace-pre-wrap">{JSON.stringify(v, null, 2)}</pre>
              ) : (
                String(v)
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/** A torn bag re-sacked with a new tag: verify the new bag, or reject it (it becomes an orphan). */
function ReplacementReview({ containerId }: { containerId: string }) {
  const decide = useReplacementDecision()
  const [ask, setAsk] = useState<'VERIFY' | 'REJECT' | null>(null)
  return (
    <div className="rounded-card border border-rule bg-surface px-4 py-3">
      <p className="text-[14px] text-ink">
        A bag was put in a new sack with a new tag. Check the weight and photos, then accept it into
        its lot or reject it.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <ActionButton
          perm="containers:decide-replacement"
          size="sm"
          onClick={() => setAsk('VERIFY')}
        >
          Accept the new bag
        </ActionButton>
        <ActionButton
          perm="containers:decide-replacement"
          size="sm"
          variant="secondary"
          onClick={() => setAsk('REJECT')}
        >
          Reject
        </ActionButton>
      </div>
      <ConfirmDialog
        open={ask !== null}
        onClose={() => setAsk(null)}
        title={ask === 'VERIFY' ? 'Accept the new bag?' : 'Reject the new bag?'}
        description={
          ask === 'VERIFY'
            ? 'The new bag joins the lot and its hold is lifted.'
            : 'The new bag leaves the lot and is kept as a bag without an owner until someone sorts it out.'
        }
        confirmLabel={ask === 'VERIFY' ? 'Accept' : 'Reject'}
        tone={ask === 'VERIFY' ? 'primary' : 'danger'}
        reason={{ label: 'Note', required: ask === 'REJECT', placeholder: 'What you checked' }}
        loading={decide.isPending}
        onConfirm={async (note) => {
          if (!ask) return
          await decide.mutateAsync({ containerId, decision: ask, note })
          setAsk(null)
        }}
      />
    </div>
  )
}
